import { ERowMovementTypes } from "api/enums";
import { IRowMovement } from "api/interfaces";
import ContractRowModel from "api/models/ContractRowModel";
import ContractRowMovimentService from "api/services/ContractRowMovimentService";
import {
  IAddAndCancelMovimentRow,
  IMovimentRow,
  IOtherChangeMovimentRow,
} from "components/interfaces";
import { makeAutoObservable } from "mobx";
import { formatNumber } from "utils";
import ContractModel from "../api/models/ContractModel";
import MeasurementService from "../api/services/MeasurementService";
import { decimal, sum, times } from "../utils/DecimalPrecision";
import { ContractIntegrationStore, ContractRowStore, ContractStore } from "./";

class Store {
  public additivesTotal = 0;
  public cancellationsTotal = 0;
  public additivesMoviments: IAddAndCancelMovimentRow[] = [];
  public cancelationsMoviments: IAddAndCancelMovimentRow[] = [];
  public otherChangesMoviments: IOtherChangeMovimentRow[] = [];
  private _canFetchBudgetTotals = false;
  private _hasExecutiveBudget = false;
  private _contractIndicators = {
    contractTotal: 0,
    billingTotal: 0,
  };
  private _budgetIndicators = {
    executiveTotal: 0,
    addedTotal: 0,
    realizedCost: 0,
  };

  constructor() {
    makeAutoObservable(this);
  }

  get hasExecutiveBudget(): boolean {
    return this._hasExecutiveBudget;
  }

  get contractIndicators() {
    const { billingTotal, contractTotal } = this._contractIndicators;
    const totalRounded = decimal(contractTotal, 2).round();

    const balanceToBill = decimal(totalRounded)
      .subtract(billingTotal)
      .toNumber();
    const billingPercentual = this.calculatePercentual(
      billingTotal,
      totalRounded
    );

    return {
      contractTotal: totalRounded,
      billingTotal,
      balanceToBill,
      billingPercentual,
    };
  }

  get budgetIndicators() {
    const { executiveTotal, addedTotal, realizedCost } = this._budgetIndicators;
    const totalRounded = decimal(executiveTotal, 2).round();

    const addedPercentual = this.calculatePercentual(addedTotal, totalRounded);
    const realizedPercentual = this.calculatePercentual(
      realizedCost,
      totalRounded
    );

    return {
      executiveTotal: totalRounded,
      addedTotal,
      realizedCost,
      addedPercentual,
      realizedPercentual,
    };
  }

  setCanFetchBudgetTotals(value: boolean) {
    this._canFetchBudgetTotals = value;
  }

  async getContractTotals() {
    this.resetContractIndicators();
    const contract = ContractStore.getCurrentContract();
    if (!contract) return;

    return this.getContractAndBillingTotals(contract);
  }

  private async getContractAndBillingTotals(contract: ContractModel) {
    const billingTotal = await MeasurementService.getBillingTotals(
      contract.uid
    );

    this._contractIndicators = {
      contractTotal: contract.total,
      billingTotal,
    };
  }

  async getExecutiveTotalsIfAuthenticated() {
    this.resetBudgetIndicators();
    if (!this._canFetchBudgetTotals) return;

    const contract = ContractStore.getCurrentContract();
    if (!contract) return;

    const budgetId = contract.executiveBudget?.id;
    if (!budgetId) return;

    if (ContractIntegrationStore.isAuthenticated) {
      this._hasExecutiveBudget = true;
      await this.getExecutiveTotal(budgetId);
    }
  }

  private async getExecutiveTotal(budgetId: string) {
    const totals = await ContractIntegrationStore.fetchBudgetTotals(budgetId);
    if (totals)
      this._budgetIndicators = {
        executiveTotal: totals.total,
        addedTotal: totals.addedTotal,
        realizedCost: totals.realizedTotal,
      };
  }

  private calculatePercentual(value, total) {
    return decimal(value, 2).dividedBy(total).toNumber();
  }

  private resetContractIndicators() {
    this._contractIndicators = {
      contractTotal: 0,
      billingTotal: 0,
    };
  }

  private resetBudgetIndicators() {
    this._hasExecutiveBudget = false;
    this._budgetIndicators = {
      executiveTotal: 0,
      addedTotal: 0,
      realizedCost: 0,
    };
  }

  public async getContractsRowsMoviments() {
    this.resetAddsAndCancelsData();

    const contract = ContractStore.getCurrentContract();
    if (!contract) return;

    const rows = await ContractRowMovimentService.getAllRowsMoviments(
      contract.uid
    );

    let addsTotal = 0;
    let cancelsTotal = 0;
    let addsList: IAddAndCancelMovimentRow[] = [];
    let cancelsList: IAddAndCancelMovimentRow[] = [];
    let othersList: IOtherChangeMovimentRow[] = [];

    rows.forEach((rowMov) => {
      const row = ContractRowStore.getRowByCode(rowMov.structured);
      if (!row) return;

      let lastQuantity = 0;
      Object.values(rowMov.data).forEach((moviment) => {
        const movTotal = times(
          moviment.quantityApplied || 0,
          moviment.unitCost || 0
        );

        switch (moviment.type) {
          case ERowMovementTypes.ADDITIVE:
            addsTotal = sum(addsTotal, movTotal);
            this.insertAddOrCancelMovimentToList(addsList, row, moviment);
            break;
          case ERowMovementTypes.CANCELLATION:
            cancelsTotal = sum(cancelsTotal, movTotal);
            this.insertAddOrCancelMovimentToList(cancelsList, row, moviment);
            break;
          case ERowMovementTypes.EDIT_DESCRIPTION:
            this.insertOhterChangeMovimentToList(othersList, row, moviment);
            break;
          case ERowMovementTypes.UPDATED:
            this.insertOhterChangeMovimentToList(
              othersList,
              row,
              moviment,
              lastQuantity
            );
            break;
          default:
            break;
        }

        if (moviment.type !== ERowMovementTypes.EDIT_DESCRIPTION) {
          lastQuantity = moviment.quantity || 0;
        }
      });
    });

    this.additivesTotal = addsTotal;
    this.cancellationsTotal = cancelsTotal;
    this.additivesMoviments = this.orderByDateAndCode(addsList).reverse();
    this.cancelationsMoviments = this.orderByDateAndCode(cancelsList).reverse();
    this.otherChangesMoviments = this.orderByDateAndCode(othersList).reverse();
  }

  private insertAddOrCancelMovimentToList(
    list: IAddAndCancelMovimentRow[],
    row: ContractRowModel,
    moviment: IRowMovement
  ) {
    const totalApplied = times(
      moviment.quantityApplied || 0,
      moviment.unitCost || 0
    );
    const dateFormated = moviment.date.toLocaleDateString("pt-br");

    list.push({
      code: row.codeStructured,
      structured: row.structured,
      description: row.description,
      date: moviment.date,
      quantityApplied: moviment.quantityApplied || 0,
      userName: moviment.user?.name || "",
      totalApplied,
      dateFormated,
    });
  }

  private insertOhterChangeMovimentToList(
    list: IOtherChangeMovimentRow[],
    row: ContractRowModel,
    moviment: IRowMovement,
    lastQuantity?: number
  ) {
    const dateFormated = moviment.date.toLocaleDateString("pt-br");
    const quantityBefore =
      lastQuantity !== undefined ? formatNumber(lastQuantity || 0, 2, 5) : "-";
    const quantityAfter =
      moviment.quantityApplied !== undefined
        ? formatNumber(moviment.quantityApplied || 0, 2, 5)
        : "-";

    list.push({
      code: row.codeStructured,
      structured: row.structured,
      descriptionBefore: moviment.oldDescription || "-",
      descriptionAfter: moviment.description || "-",
      date: moviment.date,
      quantityBefore,
      quantityAfter,
      userName: moviment.user?.name || "",
      dateFormated,
    });
  }

  private orderByDateAndCode<T extends IMovimentRow>(list: T[]) {
    return list.sort((a, b) => {
      const diffTime = a.date.getTime() - b.date.getTime();
      if (diffTime !== 0) {
        return diffTime;
      }
      return a.code.localeCompare(b.code);
    });
  }

  private resetAddsAndCancelsData() {
    this.additivesTotal = 0;
    this.cancellationsTotal = 0;
    this.additivesMoviments = [];
    this.cancelationsMoviments = [];
  }
}

const IndicatorsStore = new Store();
export default IndicatorsStore;
